import React, { useState, useContext, useEffect, createContext } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '../components/ConfirmationDialog';
import api from '../api/api';
import { AuthContext } from './AuthContext';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { useChatSession } from './ChatSessionContext';

// Create a context for the message handler
const MessageHandlerContext = createContext();

// Custom hook to easily access the message handler context
export const useMessageHandler = () => {
    return useContext(MessageHandlerContext);
};

// Provider component for the message handler context
export const MessageHandlerProvider = ({ children }) => {
    const [currentSession, setCurrentSession] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [loadingInputArea, setLoadingInputArea] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogConfig, setDialogConfig] = useState({});
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
    const { isAuthenticated, userType } = useContext(AuthContext);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [playbackData, setPlaybackData] = useState({
        duration: 0,
        currentTime: 0,
        isPlaying: false,
        audioElement: null
    });
    const [webSearchActive, setWebSearchActive] = useState(false);

    const { projects = [] } = useChatSession();

    const { t } = useTranslation();
    const isRegisteredUser = isAuthenticated && userType === 'registered';

    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPlaybackActive, setIsPlaybackActive] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [selectedSystemPrompt, setSelectedSystemPrompt] = useState('');
    const { settings } = useUserSettings();
    const systemPrompts = settings.systemPrompts || [];
    const [activeEditIndex, setActiveEditIndex] = useState(null);

    const isInteracting = isProcessing || isPlaybackActive || isTranscribing || isLoadingSession;

    // Helper function to set any interaction state
    const setInteracting = (type, value) => {
        switch (type) {
            case 'processing':
                setIsProcessing(value);
                break;
            case 'playback':
                setIsPlaybackActive(value);
                break;
            case 'transcribing':
                setIsTranscribing(value);
                break;
            case 'loading':
                setIsLoadingSession(value);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const initializeApp = async () => {
            try {
                if (isRegisteredUser) {
                    setMessages([]);
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('Error initializing app:', error);
            }
        };

        if (!isInitialized) {
            initializeApp();
        }

    }, [setMessages, isInitialized, isRegisteredUser]);

    const handleError = (message) => {
        setErrorDialog({
            open: true,
            title: 'Error',
            message: message
        });
    };

    // Function to show confirmation dialog
    const showDialog = (titleKey, contentKey, onConfirm) => {
        setDialogConfig({ titleKey, contentKey, onConfirm });
        setDialogOpen(true);
    };

    // Function to close the dialog
    const closeDialog = () => {
        setDialogOpen(false);
    };

    // Function to send a message
    const sendMessage = async (formData, setMessages, selectedEngine, selectedService) => {
        if (!formData.get('userMessage').trim() &&
            !formData.getAll('files').length &&
            !formData.get('textAttachments')) return;

        setIsProcessing(true);
        setLoadingInputArea(true);

        const files = formData.getAll('files');
        const textAttachmentsStr = formData.get('textAttachments');
        const textAttachments = textAttachmentsStr ? JSON.parse(textAttachmentsStr) : [];

        // Check file sizes before adding message to the chat
        const maxSize = 5 * 1024 * 1024; // 5MB
        const oversizedFiles = files.filter(file => file.size > maxSize);

        if (oversizedFiles.length > 0) {
            setErrorDialog({
                open: true,
                title: t('errors.uploadError'),
                message: t('errors.fileTooLarge', { maxSize: '5MB' })
            });
            setIsProcessing(false);
            setLoadingInputArea(false);
            return;
        }

        const imageData = files.map(file => ({
            file,
            originalName: file.originalName,
            mimetype: file.type,
            preview: URL.createObjectURL(file),
            base64: null
        }));

        // Extract systemPromptName from formData
        const systemPromptName = formData.get('systemPromptName');

        const userMessage = {
            role: 'user',
            content: formData.get('autoOptimizePrompt') === 'true' ? t('input.optimizationPrefix') + '\n\n' + formData.get('userMessage') : formData.get('userMessage'),
            images: imageData,
            textAttachments: textAttachments,
            service: selectedService,
            systemPromptName: systemPromptName,
        };

        setMessages((prevMessages) => [...prevMessages, userMessage]);

        let response;
        try {
            // send the message to backend and wait for reply
            response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/chat`, formData);

            if (!response.data.success) {
                let errorMessage = response.data.reply;
                if (response.data.error === 'FILE_TOO_LARGE') {
                    errorMessage = t('errors.fileTooLarge', { maxSize: '5MB' });
                }
                setErrorDialog({
                    open: true,
                    title: t('errors.uploadError'),
                    message: errorMessage
                });

                // remove the last user message
                setMessages(prev => prev.slice(0, -1));

                setIsProcessing(false);
                setLoadingInputArea(false);
                return { success: false };

            } else {

                // update user message if it was optimized on the backend side
                if (formData.get('autoOptimizePrompt') === 'true') {
                    setMessages(prev => prev.slice(0, -1));

                    userMessage.content = response.data.userMessage;
                    setMessages((prevMessages) => [...prevMessages, userMessage]);
                }

                // add the response message to the display message list
                setMessages((prevMessages) => [...prevMessages, {
                    role: 'assistant',
                    content: response.data.reply,
                    engine: selectedEngine,
                    model: response.data.model,
                    service: selectedService,
                    systemPromptName: systemPromptName,
                    usedProjectContext: response.data.usedProjectContext || false,
                    chainOfThought: response.data.chainOfThought,
                }]);

                setIsProcessing(false);
                setLoadingInputArea(false);
                return { success: true };
            }

        } catch (error) {
            console.error('Error sending message:', error);

            const errorMessage = error.response?.data?.reply || t('errors.unexpectedError');
            setErrorDialog({
                open: true,
                title: t('errors.uploadError'),
                message: errorMessage
            });

            // remove the user message that was just added
            setMessages(prev => prev.slice(0, -1));

            setIsProcessing(false);
            setLoadingInputArea(false);
            return { success: false };
        }
    };

    // Function to refresh a specific message
    const refreshMessage = async (
        index,
        customMessages,
        setMessages,
        selectedEngine,
        selectedService,
        autoOptimizePrompt = false,
        enableThinking = false,
        systemPromptName = selectedSystemPrompt) => {

        showDialog(
            t('regenerateMessage.title'),
            t('regenerateMessage.content'),
            async () => {
                try {
                    setIsProcessing(true);
                    setLoadingInputArea(true);

                    // Get current project if the session is in a project
                    const currentProject = projects?.find(project =>
                        project.sessions?.includes(currentSession)
                    );

                    const currentSystemPromptObj = systemPrompts.find(p => p.name === selectedSystemPrompt);
                    const currentSystemPromptContent = currentSystemPromptObj?.content;

                    const response = await api.post(
                        `${process.env.REACT_APP_API_BASE_URL}/api/chat/refresh/${index}`,
                        {
                            selectedEngine,
                            sessionId: currentSession,
                            systemPrompt: currentSystemPromptContent,
                            systemPromptName: selectedSystemPrompt,
                            autoOptimizePrompt: autoOptimizePrompt,
                            enableThinking: enableThinking
                        }
                    );

                    if (!response.data.success) {
                        handleError(response.data.reply);
                    }

                    setMessages(response.data.conversationHistory);

                } catch (error) {
                    console.error('Error refreshing message:', error);
                } finally {
                    setIsProcessing(false);
                    setLoadingInputArea(false);
                }
            }
        );
    };

    const handleEditMessageSave = async (
        editedContent,
        editedImages,
        editedTextAttachments,
        editedPdfAttachments,
        index,
        setIsEditing,
        restoreState,
        selectedEngine,
        selectedService,
        autoOptimizePrompt = false,
        enableThinking = false
    ) => {
        try {
            setIsProcessing(true);
            setLoadingInputArea(true);

            const formData = new FormData();
            formData.append('sessionId', currentSession);
            formData.append('userMessage', editedContent);
            formData.append('messageIndex', index);

            // Handle image attachments
            if (editedImages && editedImages.length > 0) {
                // New images that need to be uploaded
                const newImages = editedImages.filter(img => img.file && !img.attachmentId);
                newImages.forEach(img => {
                    formData.append('imageFiles', img.file);
                });

                // All image metadata (existing + new)
                formData.append('allImageAttachments', JSON.stringify(
                    editedImages.map(img => ({
                        originalName: img.originalName,
                        mimetype: img.mimetype,
                        attachmentId: img.attachmentId || '' // Empty string for new attachments
                    }))
                ));
            } else {
                formData.append('allImageAttachments', JSON.stringify([]));
            }

            // Handle text attachments
            if (editedTextAttachments && editedTextAttachments.length > 0) {
                formData.append('textAttachments', JSON.stringify(
                    editedTextAttachments.map(att => ({
                        content: att.content,
                        originalName: att.originalName,
                        type: att.type,
                        attachmentId: att.attachmentId || '' // Empty string for new attachments
                    }))
                ));
            } else {
                formData.append('textAttachments', JSON.stringify([]));
            }

            // Handle PDF attachments
            if (editedPdfAttachments && editedPdfAttachments.length > 0) {
                // New PDFs that need to be uploaded
                const newPdfs = editedPdfAttachments.filter(pdf => pdf.file && !pdf.attachmentId);
                newPdfs.forEach(pdf => {
                    formData.append('pdfFiles', pdf.file);
                });

                // All PDF metadata (existing + new)
                formData.append('allPdfAttachments', JSON.stringify(
                    editedPdfAttachments.map(pdf => ({
                        originalName: pdf.originalName,
                        mimetype: pdf.mimetype || 'application/pdf',
                        attachmentId: pdf.attachmentId || '' // Empty string for new attachments
                    }))
                ));
            } else {
                formData.append('allPdfAttachments', JSON.stringify([]));
            }

            // Send the request to update the message
            const response = await api.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/update-user-message`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                try {
                    const currentSystemPromptObj = systemPrompts.find(p => p.name === selectedSystemPrompt);
                    const currentSystemPromptContent = currentSystemPromptObj?.content;
                    const refreshResponse = await api.post(
                        `${process.env.REACT_APP_API_BASE_URL}/api/chat/refresh/${index + 1}`,
                        {
                            selectedEngine,
                            sessionId: currentSession,
                            systemPrompt: currentSystemPromptContent,
                            systemPromptName: selectedSystemPrompt,
                            service: selectedService,
                            autoOptimizePrompt: autoOptimizePrompt,
                            enableThinking: enableThinking
                        }
                    );
                    if (refreshResponse.data.success) {
                        setMessages(refreshResponse.data.conversationHistory);
                    } else {
                        throw new Error(refreshResponse.data.reply || 'Error refreshing message');
                    }
                } catch (refreshError) {
                    console.error('Error refreshing message:', refreshError);
                }
                setIsEditing(false);
            } else {
                console.error('Failed to update message:', response.data.message);
                restoreState();
            }
        } catch (error) {
            console.error('Error updating message:', error);
            restoreState();
        } finally {
            setIsProcessing(false);
            setLoadingInputArea(false);
        }
    };

    // Function to reset the chat
    const resetMessages = async (setMessages) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/reset`);
            setMessages(response.data.conversationHistory);
        } catch (error) {
            console.error('Error resetting chat:', error);
        }
    };

    // Function to optimize the prompt
    const optimizePrompt = async (userMessage, selectedEngine) => {
        if (!userMessage.trim()) return '';

        setLoadingInputArea(true);

        try {
            const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/optimize`, {
                userMessage,
                selectedEngine,
                sessionId: currentSession
            });

            if (!response.data.success) {
                handleError(response.data.reply);
                return ''
            } else {
                return response.data.reply;
            }

        } catch (error) {
            console.error('Error optimizing message:', error);
        } finally {
            setLoadingInputArea(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(chunks, { type: 'audio/mp4' });
                await handleTranscription(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            setAudioChunks(chunks);
            setMediaRecorder(recorder);
            recorder.start();
            setIsRecording(true);

        } catch (error) {
            console.error('Error starting recording:', error);
            setErrorDialog({
                open: true,
                message: 'Error starting audio recording'
            });
        }
    };

    const stopRecording = async (onTranscriptionComplete) => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                await handleTranscription(audioBlob, onTranscriptionComplete);
                mediaRecorder.stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.stop();
            setIsRecording(false);
        }
    };

    const handleTranscription = async (audioBlob, onTranscriptionComplete) => {
        try {
            setLoadingInputArea(true);
            const formData = new FormData();
            formData.append('file', audioBlob, 'audio.webm');

            const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/transcribe`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                if (onTranscriptionComplete) {
                    onTranscriptionComplete(response.data.text);
                }
            } else {
                throw new Error('Transcription failed');
            }
        } catch (error) {
            console.error('Error transcribing audio:', error);
            setErrorDialog({
                open: true,
                message: 'Error transcribing audio'
            });
            return '';
        } finally {
            setLoadingInputArea(false);
        }
    };

    const startPlayback = async (text) => {
        try {
            stopPlayback();
            setIsTranscribing(true);
            setIsProcessing(true);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/synthesize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.details || 'Failed to synthesize speech');
            }

            const audioBlob = await response.blob();
            const audio = new Audio(URL.createObjectURL(audioBlob));

            audio.addEventListener('loadedmetadata', () => {
                setIsTranscribing(false);
                setIsPlaybackActive(true);
                setPlaybackData(prev => ({
                    ...prev,
                    duration: audio.duration,
                    audioElement: audio,
                    isPlaying: true
                }));
            });

            audio.addEventListener('timeupdate', () => {
                setPlaybackData(prev => ({
                    ...prev,
                    currentTime: audio.currentTime
                }));
            });

            audio.addEventListener('ended', () => {
                setPlaybackData(prev => ({
                    ...prev,
                    isPlaying: false,
                    currentTime: 0
                }));
                setIsPlaybackActive(false);
                URL.revokeObjectURL(audio.src);
            });

            audio.addEventListener('error', (e) => {
                console.error('Audio playback error:', e);
                stopPlayback();
            });

            await audio.play();

        } catch (error) {
            console.error('Error playing audio:', error);
            setIsTranscribing(false);
            setIsPlaybackActive(false);
            setPlaybackData(prev => ({ ...prev, isPlaying: false }));
        } finally {
            setIsProcessing(false);
        }
    };

    const pausePlayback = () => {
        if (playbackData.audioElement) {
            playbackData.audioElement.pause();
            setPlaybackData(prev => ({ ...prev, isPlaying: false }));
        }
    };

    const resumePlayback = async () => {
        if (playbackData.audioElement) {
            await playbackData.audioElement.play();
            setPlaybackData(prev => ({ ...prev, isPlaying: true }));
        }
    };

    const stopPlayback = () => {
        if (playbackData.audioElement) {
            playbackData.audioElement.pause();
            playbackData.audioElement.currentTime = 0;
            URL.revokeObjectURL(playbackData.audioElement.src);
            setPlaybackData({
                duration: 0,
                currentTime: 0,
                isPlaying: false,
                audioElement: null
            });
            setIsPlaybackActive(false);
        }
    };

    const seekPlayback = (time) => {
        if (playbackData.audioElement) {
            playbackData.audioElement.currentTime = time;
            setPlaybackData(prev => ({ ...prev, currentTime: time }));
        }
    };

    return (
        <MessageHandlerContext.Provider
            value={{
                messages,
                setMessages,
                sendMessage,
                resetMessages,
                refreshMessage,
                showDialog,
                optimizePrompt,
                loadingInputArea,
                isRecording,
                startRecording,
                stopRecording,
                handleTranscription,
                playbackData,
                startPlayback,
                pausePlayback,
                resumePlayback,
                stopPlayback,
                seekPlayback,
                webSearchActive,
                setWebSearchActive,
                currentSession,
                setCurrentSession,
                isProcessing,
                isPlaybackActive,
                isTranscribing,
                isLoadingSession,
                isInteracting,
                setIsProcessing,
                setIsPlaybackActive,
                setIsTranscribing,
                setIsLoadingSession,
                setInteracting,
                selectedSystemPrompt,
                setSelectedSystemPrompt,
                systemPrompts,
                handleEditMessageSave,
                activeEditIndex,
                setActiveEditIndex,
            }}
        >
            {children}
            <ConfirmationDialog
                open={dialogOpen}
                titleKey={dialogConfig.titleKey}
                contentKey={dialogConfig.contentKey}
                onClose={closeDialog}
                onConfirm={() => {
                    dialogConfig.onConfirm();
                    closeDialog();
                }}
            />
            <ConfirmationDialog
                open={errorDialog.open}
                titleKey="Error"
                contentKey={errorDialog.message}
                onClose={() => setErrorDialog({ open: false, message: '' })}
                onConfirm={() => setErrorDialog({ open: false, message: '' })}
                showCancelButton={false}
            />
        </MessageHandlerContext.Provider>
    );
};